import { type Content, generateText, isEmptyObject } from "@tiptap/core";
import { renderToHTMLString, renderToReactElement } from "@tiptap/static-renderer";
import Image from "next/image";
import type { ProductBrowseResult } from "@/dao/product";
import { logger } from "@/lib/logger";
import { getCSSPropertiesFromString } from "@/lib/utils";
import { BlogProductEmbed } from "@/ui/storefront/blog-product-embed";
import { defaultTiptapExtensions } from "./tiptap-extensions";

export const safeGenerateText = (content: Content) => {
	if (typeof content === "string") {
		return content;
	}
	if (!content) {
		return null;
	}
	try {
		return generateText(content, defaultTiptapExtensions);
	} catch (err) {
		logger.error(`[safeGenerateText]`, err);
		return null;
	}
};

export const extractFirstParagraph = (content: Content) => {
	if (typeof content === "string") {
		return content.slice(0, 200) + (content.length > 200 ? "..." : "");
	}
	if (!content || typeof content !== "object") {
		return "";
	}

	const findFirstParagraph = (node: any): string | null => {
		if (node?.type === "paragraph" && node?.content) {
			const textContent = node.content
				.filter((item: any) => item.type === "text")
				.map((item: any) => item.text)
				.join("");
			return textContent;
		}

		if (node?.content && Array.isArray(node.content)) {
			for (const childNode of node.content) {
				const result = findFirstParagraph(childNode);
				if (result) {
					return result;
				}
			}
		}

		return null;
	};

	const firstParagraph = findFirstParagraph(content);
	if (!firstParagraph) {
		return "";
	}

	return firstParagraph.length > 200 ? firstParagraph.slice(0, 200) + "..." : firstParagraph;
};

export const calculateReadingTime = (content: Content): number => {
	if (typeof content === "string") {
		const words = content.trim().split(/\s+/).length;
		return Math.max(1, Math.ceil(words / 200)); // 200 words per minute
	}
	if (!content || typeof content !== "object") {
		return 1;
	}

	const extractText = (node: any): string => {
		let text = "";

		if (node?.type === "text" && node.text) {
			text += node.text + " ";
		}

		if (node?.content && Array.isArray(node.content)) {
			for (const childNode of node.content) {
				text += extractText(childNode);
			}
		}

		return text;
	};

	const fullText = extractText(content);
	const words = fullText.trim().split(/\s+/).length;
	return Math.max(1, Math.ceil(words / 200)); // 200 words per minute
};

export const safeRenderToReactElement = (content: Content) => {
	if (typeof content === "string") {
		return content;
	}
	if (!content) {
		return "";
	}
	try {
		return renderToReactElement({
			content,
			extensions: defaultTiptapExtensions,
			options: {
				nodeMapping: {
					image: ({ node }) => {
						return optimizeImage(node);
					},
					imageResize: ({ node }) => {
						return optimizeImage(node);
					},
					productEmbed: ({ node }) => {
						const productIds = node.attrs.productIds || [];
						const count = productIds.length;

						return (
							<div className="my-8 p-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 text-center">
								<div className="flex items-center justify-center gap-2 text-gray-600">
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
										/>
									</svg>
									<span className="font-medium">
										Featured Products ({count} product{count !== 1 ? "s" : ""})
									</span>
								</div>
								<p className="text-sm text-gray-500 mt-1">Products will appear here in the published post</p>
							</div>
						);
					},
				},
			},
		});
	} catch (err) {
		logger.error(`[safeRenderToReactElement]`, err);
		return "";
	}
};

type MappingNode = Parameters<
	NonNullable<NonNullable<Parameters<typeof renderToReactElement>[0]["options"]>["nodeMapping"]>[string]
>[0]["node"];
function optimizeImage(node: MappingNode) {
	if (!node.attrs.src) {
		return null;
	}

	const style = getCSSPropertiesFromString(node.attrs.style || node.attrs.containerStyle);

	const sizes = style.width
		? `(max-width: ${style.width}) 100vw, ${style.width}`
		: // @todo fallback; can we infer this any better?
			`(max-width: 1024px) 100vw, (max-width: 1280px) 50vw, 700px`;

	const commonProps = {
		src: node.attrs.src as string,
		width: node.attrs.width,
		height: node.attrs.height,
		style: style,
		sizes: sizes,
	};

	if (node.attrs.width && node.attrs.height) {
		return <Image {...commonProps} alt={node.attrs.alt ?? ""} />;
	} else {
		return (
			// biome-ignore lint/performance/noImgElement: no width or height
			<img {...commonProps} alt={node.attrs.alt ?? ""} srcSet={node.attrs.srcset} />
		);
	}
}
export const safeRenderBlogContentToReactElement = (
	content: Content,
	context: {
		domain: string;
		store: {
			id: string;
			currency: string;
			locale: string;
			taxBehavior: "inclusive" | "exclusive";
			flags: any;
		};
		products?: ProductBrowseResult["data"];
	},
) => {
	if (typeof content === "string") {
		return content;
	}
	if (!content) {
		return "";
	}
	try {
		return renderToReactElement({
			content,
			extensions: defaultTiptapExtensions,
			options: {
				nodeMapping: {
					image: ({ node }) => {
						if (!node.attrs.src) {
							return null;
						}

						const style = getCSSPropertiesFromString(node.attrs.style);

						const sizes = style.width
							? `(max-width: ${style.width}) 100vw, ${style.width}`
							: // @todo fallback; can we infer this any better?
								`(max-width: 1024px) 100vw, (max-width: 1280px) 50vw, 700px`;

						const commonProps = {
							src: node.attrs.src as string,
							width: node.attrs.width,
							height: node.attrs.height,
							style: style,
							sizes: sizes,
						};

						if (node.attrs.width && node.attrs.height) {
							return <Image {...commonProps} alt={node.attrs.alt ?? ""} />;
						} else {
							return (
								// biome-ignore lint/performance/noImgElement: no width or height
								<img {...commonProps} alt={node.attrs.alt ?? ""} srcSet={node.attrs.srcset} />
							);
						}
					},
					productEmbed: ({ node }) => {
						const productIds = node.attrs.productIds || [];

						if (!productIds.length) {
							return null;
						}

						return (
							<BlogProductEmbed
								productIds={productIds}
								domain={context.domain}
								store={context.store}
								products={context.products}
							/>
						);
					},
				},
			},
		});
	} catch (err) {
		logger.error(`[safeRenderBlogContentToReactElement]`, err);
		return "";
	}
};

/**
 * @deprecated use `safeRenderToReactElement` instead
 */
export const safeGenerateHTML = (content: Content) => {
	if (typeof content === "string") {
		return content;
	}
	if (!content) {
		return "";
	}
	try {
		return renderToHTMLString({ content, extensions: defaultTiptapExtensions });
	} catch (err) {
		logger.error(`[safeGenerateHTML]`, err);
		return "";
	}
};

export const isContentEmpty = (
	content: unknown,
): content is null | undefined | "" | Record<string, never> => {
	if (!content || isEmptyObject(content)) {
		return true;
	}
	const text = safeGenerateText(content);
	return !text?.trim();
};

export const defaultContent = {
	type: "doc",
	content: [
		{
			type: "paragraph",
			content: [
				{
					text: " ",
					type: "text",
				},
			],
		},
	],
};
